import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { toast } from 'react-toastify';
import BackButton from '../../components/BackButton';
import { fetchMemberAccountLedger } from '../../controllers/memberAccountController';
import { cancelMeetingMeal, fetchMeetingMealById } from '../../controllers/meetingMealController';
import {
  LogoPostStatusNames,
  MeetingMealStatusNames,
  canCancelMeetingMeal,
  getMeetingMealStatusChipSx,
} from './meetingMealsConstants';
import { formatMealDate, formatMealMoney, formatPeriodLabel } from './meetingMealsFormUtils';

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '5px' },
  '& .MuiInputBase-input': { fontSize: '14px' },
};

const Row = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>{label}</Typography>
    <Typography sx={{ fontSize: '14px', color: '#444', textAlign: 'right' }}>{value}</Typography>
  </Stack>
);

const WeeklyMeetingMealDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const loadLedger = useCallback(async (memberId, period) => {
    if (!memberId) return;
    setLedgerLoading(true);
    try {
      const { data } = await fetchMemberAccountLedger({
        memberId,
        sourceTypes: [6],
        period: period || null,
        pageNumber: 1,
        pageSize: 100,
        includeSpouse: false,
        includeEventGuests: false,
        includeMealGuests: false,
      });
      setLedgerRows(Array.isArray(data) ? data : []);
    } catch {
      setLedgerRows([]);
    } finally {
      setLedgerLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchMeetingMealById(id);
      setItem(data);
      await loadLedger(data.memberId, data.period);
    } catch (e) {
      setItem(null);
      setLedgerRows([]);
      setError(e?.message || 'Yemek borcu bulunamadı');
    } finally {
      setLoading(false);
    }
  }, [id, loadLedger]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await cancelMeetingMeal({
        meetingMealId: item.id,
        cancellationReason: cancelReason?.trim() || null,
      });
      toast.success('Yemek borcu iptal edildi');
      setCancelOpen(false);
      load();
    } catch (e) {
      toast.error(e?.message || 'İptal edilemedi');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !item) {
    return (
      <Stack spacing={2}>
        <BackButton to="/payments/weekly-meetings" />
        <Alert severity="error">{error || 'Yemek borcu bulunamadı'}</Alert>
      </Stack>
    );
  }

  const statusChip = getMeetingMealStatusChipSx(item.status);
  const logoError = item.logoPostStatus === 3 && item.logoPostError;

  return (
    <Stack spacing={2}>
      <BackButton to="/payments/weekly-meetings" />
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
        <Box>
          <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#444' }}>
            {item.memberName || 'Yemek Borcu'}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap">
            <Chip label={item.statusName || MeetingMealStatusNames[item.status]} size="small" sx={{ ...statusChip, fontWeight: 600 }} />
            <Typography sx={{ fontSize: '14px', color: '#666' }}>
              Dönem: {formatPeriodLabel(item.period)}
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#0E4D94' }}>
              {formatMealMoney(item.amount, item.currency)}
            </Typography>
          </Stack>
          {item.title && (
            <Typography sx={{ fontSize: '14px', color: '#666', mt: 0.5 }}>{item.title}</Typography>
          )}
          {item.memberId && (
            <Button
              size="small"
              onClick={() => navigate(`/members/${item.memberId}`)}
              sx={{ mt: 1, textTransform: 'none', px: 0 }}
            >
              Üye detayına git
            </Button>
          )}
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {canCancelMeetingMeal(item.status) && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<CancelIcon />}
              onClick={() => setCancelOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              İptal
            </Button>
          )}
        </Stack>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ boxShadow: 'none', borderColor: '#ececec' }}>
            <CardContent>
              <Typography sx={{ fontSize: '15px', fontWeight: 600, mb: 2 }}>Tarihler</Typography>
              <Stack spacing={1}>
                <Row label="Vade" value={formatMealDate(item.dueDate)} />
                <Row label="Son ödeme" value={formatMealDate(item.finalDueDate)} />
                <Row label="Oluşturulma" value={formatMealDate(item.createdAt)} />
                <Row label="Açıklama" value={item.description || '-'} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ boxShadow: 'none', borderColor: '#ececec' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <AccountBalanceIcon sx={{ color: '#0E4D94' }} />
                <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>Logo borç fişi</Typography>
              </Stack>
              <Stack spacing={1}>
                <Row label="Fiş no" value={item.logoSlipNo || '-'} />
                <Row label="Cari kod" value={item.logoDebtArpCode || '-'} />
                <Row label="Durum" value={item.logoPostStatusName || LogoPostStatusNames[item.logoPostStatus] || '-'} />
                {logoError && <Alert severity="error" sx={{ mt: 1 }}>{item.logoPostError}</Alert>}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ boxShadow: 'none', borderColor: '#ececec' }}>
        <CardContent>
          <Typography sx={{ fontSize: '15px', fontWeight: 600, mb: 2 }}>Borç satırları</Typography>
          {ledgerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : ledgerRows.length === 0 ? (
            <Alert severity="info">Bu dönem için borç satırı bulunamadı.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#a3a3a3', fontWeight: 500 }}>Tarih</TableCell>
                    <TableCell sx={{ color: '#a3a3a3', fontWeight: 500 }}>Açıklama</TableCell>
                    <TableCell align="right" sx={{ color: '#a3a3a3', fontWeight: 500 }}>Borç</TableCell>
                    <TableCell align="right" sx={{ color: '#a3a3a3', fontWeight: 500 }}>Ödeme</TableCell>
                    <TableCell align="right" sx={{ color: '#a3a3a3', fontWeight: 500 }}>Bakiye</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ledgerRows.map((row, idx) => (
                    <TableRow key={row.id || `${row.transactionDate}-${idx}`}>
                      <TableCell>{formatMealDate(row.transactionDate)}</TableCell>
                      <TableCell>{row.description || row.title || '-'}</TableCell>
                      <TableCell align="right">
                        {row.debitAmount != null ? formatMealMoney(row.debitAmount, row.currency) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {row.creditAmount != null ? formatMealMoney(row.creditAmount, row.currency) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {row.balance != null ? formatMealMoney(row.balance, row.currency) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', pr: 3 }}>
          Yemek Borcunu İptal Et
          <IconButton onClick={() => setCancelOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bu işlem tüm borç başlığını iptal eder. Yalnızca bekleyen kayıtlar iptal edilebilir.
          </Alert>
          <TextField
            label="İptal nedeni"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={2}
            fullWidth
            sx={fieldSx}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} sx={{ textTransform: 'none' }}>Vazgeç</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCancel}
            disabled={actionLoading}
            sx={{ textTransform: 'none' }}
          >
            İptal Et
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default WeeklyMeetingMealDetail;
